/**
 * Cache HTTP Interceptor
 *
 * Intercepts HTTP requests to check cache before making network calls.
 * Only caches GET requests to ensure idempotency.
 *
 * Features:
 * - Checks cache before making HTTP request
 * - Returns cached response immediately if valid
 * - Stores successful GET responses in cache with URL-based TTL
 * - Skips non-GET requests and non-API URLs
 * - Integrates with CacheService for TTL and eviction
 *
 * Interceptor Order: CACHE → AUTH → RETRY
 * Cache checks happen first to avoid unnecessary auth overhead.
 *
 * @see VEO-087 Sprint 2 Plan
 */
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, filter } from 'rxjs/operators';
import { CacheService, generateCacheKey, getTtlForUrl } from '../services/cache.service';
import { isConfiguredApiUrl } from './api-url.util';

/**
 * Check if request is cacheable
 *
 * @param req HTTP request
 * @returns True if request should be cached
 */
function isCacheable(req: HttpRequest<unknown>): boolean {
  // Only cache GET requests (idempotent)
  if (req.method !== 'GET') {
    return false;
  }

  // Only cache API requests
  if (!isConfiguredApiUrl(req.url)) {
    return false;
  }

  // Skip requests with cache bypass header
  if (req.headers.has('X-Skip-Cache')) {
    console.log('[CacheInterceptor] Skipping cache due to X-Skip-Cache header:', req.url);
    return false;
  }

  return true;
}

/**
 * Cache interceptor implementation
 */
export const cacheInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  // Skip non-cacheable requests
  if (!isCacheable(req)) {
    return next(req);
  }

  const cacheService = inject(CacheService);

  // Generate cache key
  const cacheKey = generateCacheKey(req.method, req.url, req.params);

  // Check cache
  const cachedResponse = cacheService.get<any>(cacheKey);
  if (cachedResponse !== null) {
    console.log('[CacheInterceptor] Cache HIT:', cacheKey);
    // Return cached response
    return of(
      new HttpResponse({
        body: cachedResponse,
        status: 200,
        statusText: 'OK (cached)',
        url: req.url,
      })
    );
  }

  console.log('[CacheInterceptor] Cache MISS:', cacheKey);

  // Make HTTP request and cache response
  return next(req).pipe(
    filter((event): event is HttpResponse<any> => event instanceof HttpResponse),
    tap((response) => {
      // Only cache successful responses
      if (response.status === 200 && response.body) {
        const ttl = getTtlForUrl(req.url);
        cacheService.set(cacheKey, response.body, ttl);
        console.log(`[CacheInterceptor] Cached response: ${cacheKey} (TTL: ${ttl}ms)`);
      }
    })
  );
};
